import { REWARD_TYPES } from '../assets/js/rewards.js'
import { shopItemArtwork } from './assets.js'

import creditsIcon from '../assets/images/currency/256w/credits.png'
import goldIcon from '../assets/images/currency/256w/gold.png'

/**
 * How a level reward is worded and pictured.
 *
 * Two places render rewards — the track on the Overview panel, and the
 * celebration the shell shows after a claim — and they have to agree: the
 * thing you saw on the card is the thing you just won. They were the same
 * component until the profile split into routes, and the moment they weren't,
 * this was a copy waiting to drift.
 *
 * Pure functions of a reward. Nothing here reads component state.
 */

export function describeReward(reward) {
    // A claimed random draw comes back with the item it resolved to
    // (see grantClaims). Naming it beats "Random Rare item", which
    // tells a player what kind of thing they won but never which.
    if (reward.item && reward.item.friendlyName) return reward.item.friendlyName

    switch (reward.type) {
        case REWARD_TYPES.Credits: return `${reward.value.toLocaleString()} credits`
        case REWARD_TYPES.Gold: return `${reward.value} gold`
        // `value` is the rarity tier for a random item, not a quantity.
        case REWARD_TYPES.RandomItem: return `Random ${reward.value} item`
        case REWARD_TYPES.Item: return reward.value
        default: return 'Reward'
    }
}

/**
 * A shop item's artwork, or null if it has none we can resolve.
 *
 * Same 256w set the shop renders from. The path is built at build
 * time by webpack (it compiles the whole directory into a context),
 * so a missing file throws here rather than 404ing at runtime — hence
 * the catch, which drops back to the rarity gem.
 */
export function shopItemIcon(item) {
    // Deliberately the null-returning variant: a reward with no
    // artwork is better served by a gem in its rarity colour than by
    // a neutral placeholder, and only the caller knows that.
    return shopItemArtwork(item)
}
export function rewardIcon(reward) {
    if (reward.type === REWARD_TYPES.Credits) return creditsIcon
    if (reward.type === REWARD_TYPES.Gold) return goldIcon
    // A resolved item shows its own artwork; anything else falls
    // through to a <rarity-gem>.
    if (reward.item) return shopItemIcon(reward.item)
    return null
}

/** Which of the three shapes a reward takes in the celebration. */
export function rewardKind(reward) {
    if (reward.type === REWARD_TYPES.Credits) return 'credits'
    if (reward.type === REWARD_TYPES.Gold) return 'gold'
    return 'item'
}

/**
 * The quantity to count up to, or null for anything that isn't a
 * quantity. An item reward's `value` is a rarity tier or a name, not
 * a number — counting up to "mythical" is not a thing.
 */
export function rewardAmount(reward) {
    const countable = reward.type === REWARD_TYPES.Credits || reward.type === REWARD_TYPES.Gold
    return countable ? reward.value : null
}

/** The word under a counted number: "+280" over "credits". */
export function rewardUnit(reward) {
    if (reward.type === REWARD_TYPES.Credits) return 'credits'
    if (reward.type === REWARD_TYPES.Gold) return 'gold'
    return null
}

/**
 * The rarity tier to draw an item reward's gem in, or null if this
 * isn't an item reward. A random-item drop carries its tier in
 * `value`; a specific item doesn't say, so it takes the default gem.
 */
export function gemRarity(reward) {
    // The resolved item knows its own tier — better than the tier the
    // reward asked for, and the only source for a plain item reward.
    if (reward.item && reward.item.tier) return reward.item.tier
    if (reward.type === REWARD_TYPES.RandomItem) return reward.value
    if (reward.type === REWARD_TYPES.Item) return 'rare'
    return null
}

/**
 * Card-sized label. The icon already says what kind of reward it is,
 * so this carries only the amount — "790", not "790 credits".
 */
export function shortReward(reward) {
    switch (reward.type) {
        case REWARD_TYPES.Credits: return reward.value.toLocaleString()
        case REWARD_TYPES.Gold: return String(reward.value)
        // `value` is the rarity tier here, not a quantity — so this
        // reads "Rare Item", not a bare "Rare".
        case REWARD_TYPES.RandomItem: return `${capitalise(reward.value)} Item`
        default: return 'Item'
    }
}

export function capitalise(text) {
    return String(text).charAt(0).toUpperCase() + String(text).slice(1)
}
